import { bulkPdfExportManageFragment } from "bulk-pdf-export/routes/fragments";
import {
  learningObjectivesIcon,
  manageIcon,
  messageIcon,
  toClassicIcon,
} from "common/defaultIcons";
import useHelpers from "composables/useHelpers";
import { transitionMeetingManageFragment } from "meetings/advisory/routes/fragments";
import { groupMeetingManageFragment } from "meetings/group-meeting/routes/fragments";
import { studentMeetingManageFragment } from "meetings/student-meeting/routes/fragments";
import { LAYOUTS, ROUTES, USER_ROLES } from "shared/constants";
import { useModuleStore } from "store/modules/moduleStore";
import {
  getRouter,
  isRouteAccessibleByName,
  pickFirstAccessibleChild,
} from "../routeAccess";

const { mappedTerms, checkRole } = useHelpers();

const resolveManageRedirect = () => {
  const router = getRouter();

  if (!router) {
    return { name: "Unauthorized" };
  }

  const { hasSettingEnabled } = useModuleStore();

  if (
    checkRole("decaan") &&
    hasSettingEnabled("portfolio") &&
    isRouteAccessibleByName(router, "manageSettingsPortfolio")
  ) {
    return { name: "manageSettingsPortfolio" };
  }

  if (
    !checkRole("decaan") &&
    isRouteAccessibleByName(router, "manageSettingsExpertGroups")
  ) {
    return { name: "manageSettingsExpertGroups" };
  }

  return pickFirstAccessibleChild(router, "manage") ?? { name: "Unauthorized" };
};

const routes = [
  {
    name: "meetingsOverview",
    path: "/meetings",
    redirect: {
      name: "manageMeetings",
    },
    meta: {
      title: "Besprekingen",
      isMenuItem: true,
      menuSection: "organise",
      // "Organiseren" reads Besprekingen, Actieplannen, Gesprekken, Taken, Leerdoelen.
      menuOrder: 1,
      icon: toClassicIcon(messageIcon),
      roleRequired: ["webmaster", "manager", "organizer"],
      settingRequired: ["studentMeeting"],
      exactRouteIdentifier: "manageMeetings",
    },
    children: [
      {
        name: "manageMeetings",
        path: "overview",
        component: () => import("pages/manage/meeting/Meetings.vue"),
        meta: {
          title: "Besprekingen",
          roleRequired: ["webmaster", "manager", "organizer"],
          settingRequired: ["studentMeeting"],
          exactRouteIdentifier: "manageMeetings",
        },
      },
      ...studentMeetingManageFragment,
      ...groupMeetingManageFragment,
      ...transitionMeetingManageFragment,
    ],
  },
  {
    name: "manageLearningObjectives",
    path: "/learning-objectives",
    redirect: {
      name: "learningObjectivesPlanner",
    },
    meta: {
      title: "Leerdoelen",
      sublabel: "bèta",
      icon: toClassicIcon(learningObjectivesIcon),
      isMenuItem: true,
      menuSection: "organise",
      menuOrder: 5,
      roleRequired: [
        "webmaster",
        "manager",
        "organizer",
        "teamleader",
        "decaan",
        "teacher",
      ],
      settingRequired: ["learningObjectives"],
    },
    children: [
      {
        path: "planner",
        name: "learningObjectivesPlanner",
        component: () => import("pages/learning-objectives/Planner.vue"),
        meta: {
          title: "Planner",
          isMenuItem: true,
          menuSection: "organise",
          inDropdown: true,
          layout: LAYOUTS.FULL_SCREEN,
        },
      },
      {
        path: "cluster-library",
        name: ROUTES.LEARNING_OBJECTIVES_CLUSTER_LIBRARY,
        component: () => import("pages/learning-objectives/ClusterLibrary.vue"),
        meta: {
          title: "Clusterbibliotheek",
          inDropdown: true,
          menuSection: "organise",
          isMenuItem: true,
          layout: LAYOUTS.PORTFOLIO,
          exactRouteIdentifier: ROUTES.LEARNING_OBJECTIVES_CLUSTER_LIBRARY,
        },
      },
      {
        path: "cluster-library/add",
        name: "addClusterLibraryCluster",
        component: () =>
          import("pages/learning-objectives/ClusterEditPage.vue"),
        meta: {
          title: "Cluster toevoegen",
          roleRequired: [
            "webmaster",
            "manager",
            "organizer",
            "teamleader",
            "decaan",
            "teacher",
          ],
          settingRequired: ["learningObjectives"],
          layout: LAYOUTS.PORTFOLIO,
          hideSubmenu: true,
          exactRouteIdentifier: ROUTES.LEARNING_OBJECTIVES_CLUSTER_LIBRARY,
        },
      },
      {
        path: "cluster-library/:clusterId(\\d+)",
        name: "editLibraryCluster",
        component: () =>
          import("pages/learning-objectives/ClusterEditPage.vue"),
        meta: {
          title: "Cluster bewerken",
          roleRequired: [
            "webmaster",
            "manager",
            "organizer",
            "teamleader",
            "decaan",
            "teacher",
          ],
          settingRequired: ["learningObjectives"],
          layout: LAYOUTS.PORTFOLIO,
          hideSubmenu: true,
          exactRouteIdentifier: ROUTES.LEARNING_OBJECTIVES_CLUSTER_LIBRARY,
        },
      },
      {
        path: "cluster-library/:clusterId(\\d+)/view",
        name: "viewLibraryCluster",
        component: () =>
          import("pages/learning-objectives/ClusterEditPage.vue"),
        meta: {
          title: "Cluster bekijken",
          roleRequired: [
            "webmaster",
            "manager",
            "organizer",
            "teamleader",
            "decaan",
            "teacher",
          ],
          settingRequired: ["learningObjectives"],
          layout: LAYOUTS.PORTFOLIO,
          hideSubmenu: true,
          readonly: true,
          exactRouteIdentifier: ROUTES.LEARNING_OBJECTIVES_CLUSTER_LIBRARY,
        },
      },
      {
        path: "learning-objectives-library",
        name: ROUTES.LEARNING_OBJECTIVES_LIBRARY,
        component: () =>
          import("pages/learning-objectives/LearningObjectivesLibrary.vue"),
        meta: {
          title: "Leerdoelen",
          inDropdown: true,
          menuSection: "organise",
          isMenuItem: true,
          layout: LAYOUTS.PORTFOLIO,
          exactRouteIdentifier: ROUTES.LEARNING_OBJECTIVES_LIBRARY,
        },
      },
      {
        path: "learning-objectives-library/add",
        name: "addLearningObjective",
        component: () =>
          import("pages/learning-objectives/LearningObjectiveEditPage.vue"),
        meta: {
          title: "Leerdoel toevoegen",
          roleRequired: [
            "webmaster",
            "manager",
            "organizer",
            "teamleader",
            "decaan",
            "teacher",
          ],
          settingRequired: ["learningObjectives"],
          layout: LAYOUTS.PORTFOLIO,
          hideSubmenu: true,
          exactRouteIdentifier: ROUTES.LEARNING_OBJECTIVES_LIBRARY,
        },
      },
      {
        path: "learning-objectives-library/:learningObjectiveId(\\d+)",
        name: "editLearningObjective",
        component: () =>
          import("pages/learning-objectives/LearningObjectiveEditPage.vue"),
        meta: {
          title: "Leerdoel bewerken",
          roleRequired: [
            "webmaster",
            "manager",
            "organizer",
            "teamleader",
            "decaan",
            "teacher",
          ],
          settingRequired: ["learningObjectives"],
          layout: LAYOUTS.PORTFOLIO,
          hideSubmenu: true,
          exactRouteIdentifier: ROUTES.LEARNING_OBJECTIVES_LIBRARY,
        },
      },
      {
        path: "learning-objectives-library/:learningObjectiveId(\\d+)/view",
        name: "viewLearningObjective",
        component: () =>
          import("pages/learning-objectives/LearningObjectiveEditPage.vue"),
        meta: {
          title: "Leerdoel bekijken",
          roleRequired: [
            "webmaster",
            "manager",
            "organizer",
            "teamleader",
            "decaan",
            "teacher",
          ],
          settingRequired: ["learningObjectives"],
          layout: LAYOUTS.PORTFOLIO,
          hideSubmenu: true,
          readonly: true,
          exactRouteIdentifier: ROUTES.LEARNING_OBJECTIVES_LIBRARY,
        },
      },
    ],
  },
  {
    name: "manage",
    path: "/manage",
    redirect: () => resolveManageRedirect(),
    meta: {
      title: "Schoolbeheer",
      roleRequired: [
        "webmaster",
        "manager",
        "organizer",
        "decaan",
        USER_ROLES.TEAMLEADER,
      ],
      isMenuItem: true,
      menuSection: "manage",
      icon: toClassicIcon(manageIcon),
      menuOrder: 1,
      layout: LAYOUTS.MAIN_NO_WRAPPER,
    },
    children: [
      {
        name: "manageSettings",
        path: "settings",
        meta: {
          title: "Schoolinstellingen",
          isMenuItem: true,
          menuSection: "manage",
          exactRouteIdentifier: "manageSchoolSettings",
          roleRequired: ["webmaster", "manager", "organizer", "decaan"],
        },
        children: [
          {
            path: "experts",
            name: "manageSettingsExpertGroups",
            meta: {
              title: "Instellingen expertgroep",
              inDropdown: true,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
            component: () => import("pages/manage/schoolsettings/Experts.vue"),
          },
          {
            path: "general",
            name: "manageSettingsGeneral",
            meta: {
              title: "Instellingen algemeen",
              inDropdown: true,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
            component: () => import("pages/manage/schoolsettings/General.vue"),
          },
          {
            path: "conversations",
            name: "manageSettingsConversations",
            // Deliberately no `settingRequired: ["conversations"]`, unlike the tabs
            // below. This tab holds the only toggle for that setting, so gating it on
            // the setting would hide the page the moment gesprekken are switched off,
            // leaving no way to switch them back on. Every school has gesprekken
            // available anyway -- see CONVERSATION-SETTING-ONLY-GATE-001.
            meta: {
              title: "Instellingen gesprekken",
              inDropdown: true,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
            component: () =>
              import("conversations/pages/ConversationSettingsPage.vue"),
          },
          {
            path: "meetings",
            name: "manageSettingsMeetings",
            meta: {
              title: "Instellingen besprekingen",
              inDropdown: true,
              menuSection: "manage",
              settingRequired: ["studentMeeting"],
              roleRequired: ["webmaster", "manager", "organizer"],
            },
            component: () => import("pages/manage/schoolsettings/Meetings.vue"),
          },
          {
            path: "portfolio",
            name: "manageSettingsPortfolio",
            meta: {
              title: "Instellingen portfolio",
              inDropdown: true,
              menuSection: "manage",
              settingRequired: ["portfolio"],
              roleRequired: ["webmaster", "manager", "organizer", "decaan"],
            },
            component: () =>
              import("portfolio/pages/PortfolioSettingsPage.vue"),
          },
          {
            path: "learning-objectives",
            name: "mangeSettingsLearningObjectives",
            meta: {
              title: "Instellingen leerdoelen",
              sublabel: "bèta",
              inDropdown: true,
              menuSection: "manage",
              settingRequired: ["learningObjectives"],
              roleRequired: ["webmaster", "manager", "organizer"],
            },
            component: () =>
              import("pages/manage/schoolsettings/LearningObjectives.vue"),
          },
          {
            path: "email",
            name: "manageSettingsNotifications",
            meta: {
              title: "Instellingen e-mail",
              inDropdown: true,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
            component: () => import("pages/manage/schoolsettings/Email.vue"),
          },
        ],
      },
      {
        // A sibling of manageSettings rather than a child: as a child it would
        // render inside the settings tab bar, and this is a page of its own.
        path: "settings/portfolio/themes",
        name: "manageSettingsPortfolioThemes",
        meta: {
          title: "Thema's",
          settingRequired: ["portfolio"],
          roleRequired: ["webmaster", "manager", "organizer", "decaan"],
          hideSubmenu: true,
          // The page renders its own breadcrumb and title.
          hideTitle: true,
        },
        component: () => import("portfolio/pages/PortfolioThemesPage.vue"),
      },
      ...bulkPdfExportManageFragment,
      {
        path: "data",
        name: "data",
        redirect: {
          name: "ViewStudents",
        },
        meta: {
          title: "Data beheren",
          roleRequired: ["webmaster", "manager", "organizer"],
          isMenuItem: true,
          menuSection: "manage",
          exactRouteIdentifier: "manageSchoolData",
        },
        children: [
          {
            path: "view-students",
            name: "ViewStudents",
            component: () => import("pages/manage/data/ViewStudents.vue"),
            meta: {
              title: `${mappedTerms.students[0].toUpperCase()}${mappedTerms.students.slice(
                1,
              )} beheren`,
              inDropdown: true,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "import-students",
            name: "ImportStudents",
            component: () =>
              import("pages/manage/data/import/ImportStudents.vue"),
            meta: {
              title: `${mappedTerms.students[0].toUpperCase()}${mappedTerms.students.slice(
                1,
              )} importeren`,
              inDropdown: true,
              menuSection: "manage",
              settingRequired: ["manageData"],
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "remove-students",
            name: "RemoveStudents",
            component: () =>
              import("pages/manage/data/import/RemoveStudents.vue"),
            meta: {
              title: `${mappedTerms.students[0].toUpperCase()}${mappedTerms.students.slice(
                1,
              )} verwijderen`,
              inDropdown: true,
              menuSection: "manage",
              settingRequired: ["manageData"],
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "upload-profile-picture",
            name: "UploadProfilePicture",
            component: () =>
              import("pages/manage/data/UploadProfilePicture.vue"),
            meta: {
              title: `${mappedTerms.student[0].toUpperCase()}${mappedTerms.student.slice(
                1,
              )} pasfoto's`,
              inDropdown: true,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "view-grades",
            name: "ViewGrades",
            component: () => import("pages/manage/data/ViewGrades.vue"),
            meta: {
              title: "Cijfers inzien",
              inDropdown: true,
              settingRequired: ["gradesEnabled"],
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "view-employees",
            name: "ViewEmployees",
            component: () => import("pages/manage/data/ViewEmployees.vue"),
            meta: {
              title: "Medewerkers beheren",
              inDropdown: true,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "import-employees",
            name: "ImportEmployees",
            component: () =>
              import("pages/manage/data/import/ImportEmployees.vue"),
            meta: {
              title: "Medewerkers importeren",
              inDropdown: true,
              menuSection: "manage",
              settingRequired: ["manageData"],
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "view-schedules",
            name: "ViewSchedules",
            component: () => import("pages/manage/data/ViewSchedules.vue"),
            meta: {
              title: "Lesgroepen bewerken",
              inDropdown: true,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "view-subjects",
            name: "ViewSubjects",
            component: () => import("pages/manage/data/ViewSubjects.vue"),
            meta: {
              title: "Vakken bewerken",
              inDropdown: true,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "import-schedules",
            name: "ImportSchedules",
            component: () =>
              import("pages/manage/data/import/ImportSchedules.vue"),
            meta: {
              title: "Vakkenpakket importeren",
              inDropdown: true,
              settingRequired: ["manageData"],
              menuSection: "manage",
              roleRequired: ["webmaster", "manager", "organizer"],
            },
          },
          {
            path: "add-student",
            name: "addStudent",
            component: () => import("pages/manage/data/AddOrUpdateStudent.vue"),
            meta: {
              title: `${mappedTerms.students[0].toUpperCase()}${mappedTerms.students.slice(
                1,
              )} toevoegen`,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager"],
            },
          },
          {
            path: "add-schedule",
            name: "AddSchedule",
            component: () =>
              import("pages/manage/data/AddOrUpdateSchedule.vue"),
            meta: {
              title: "Lesgroep toevoegen",
              menuSection: "manage",
              roleRequired: ["webmaster", "manager"],
            },
          },
          {
            path: "transfer-students",
            name: "TransferStudents",
            component: () => import("pages/manage/data/TransferStudents.vue"),
            meta: {
              title: `${mappedTerms.students[0].toUpperCase()}${mappedTerms.students.slice(
                1,
              )} verplaatsen`,
              menuSection: "manage",
              roleRequired: ["webmaster", "manager"],
            },
          },
        ],
      },
      {
        path: "connect",
        name: "connect",
        component: () => import("pages/manage/Connect.vue"),
        meta: {
          title: "Koppeling beheren",
          menuSection: "manage",
          roleRequired: ["webmaster", "manager"],
          isMenuItem: true,
        },
      },
      {
        path: "schoolsynclog",
        name: "schoolsynclog",
        component: () => import("pages/manage/SchoolSyncLog.vue"),
        meta: {
          title: "Sync Log",
          menuSection: "manage",
          roleRequired: ["webmaster", "manager", "organizer"],
          isMenuItem: true,
        },
      },
      {
        path: "schoollog",
        name: "schoollog",
        component: () => import("pages/manage/SchoolLog.vue"),
        meta: {
          title: "Log",
          menuSection: "manage",
          roleRequired: ["webmaster", "manager", "organizer"],
          isMenuItem: true,
        },
      },
    ],
  },
];

export default routes;
